import React, {useState, useEffect, useRef} from 'react';
import Check from '../../assets/icons/Check.svg';
import Plus from '../../assets/icons/Plus.svg';
import Pin from '../../assets/icons/Map_pin.svg';
import Clock from '../../assets/icons/Clock.svg';
import Sun from '../../assets/icons/Sun.svg';
import apiClient from '../../context/apiClient';

const languageFlags = {
    DE: "🇩🇪", EN: "🇬🇧", FR: "🇫🇷", ES: "🇪🇸", IT: "🇮🇹",
    NE: "🇳🇱", PL: "🇵🇱", TR: "🇹🇷", PT: "🇵🇹", RU: "🇷🇺",
    AR: "🇸🇦", ZH: "🇨🇳", JA: "🇯🇵", KO: "🇰🇷", SE: "🇸🇪",
    NO: "🇳🇴", FI: "🇫🇮", DK: "🇩🇰", HU: "🇭🇺", CZ: "🇨🇿",
    SK: "🇸🇰", RO: "🇷🇴", BG: "🇧🇬", HR: "🇭🇷", EL: "🇬🇷",
};

const roles = [
    {label: 'Instruktor:in', key: 'INSTRUCTOR'},
    {label: 'Mechaniker:in', key: 'MECHANIC'},
    {label: 'Guestmanager', key: 'GUEST_MANAGER'},
];

const getRoleLabel = (roleKey) => {
    const found = roles.find(r => r.key === roleKey);
    return found ? found.label : roleKey; // fallback to key if not found
};

const formatAddress = (address) => {
    if (!address || typeof address !== 'object') return '';
    const {street, streetNumber, postalCode, city, country} = address;
    return `${street} ${streetNumber}\n${postalCode} ${city}\n${country}`;
};

const stringifyAddress = (address) => {
    if (!address) return '';
    const {street, streetNumber, postalCode, city, country} = address;
    return `${street}, ${streetNumber}, ${postalCode}, ${city}, ${country}`;
};

const NewEmployeeCard = ({role, onEmployeeAdded}) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [showLanguageSelect, setShowLanguageSelect] = useState(false);
    const dropdownRef = useRef(null);
    const [errors, setErrors] = useState({});
    const cardRef = useRef(null);
    const [showQualificationModal, setShowQualificationModal] = useState(false);
    const [allQualifications, setAllQualifications] = useState([]);
    const [qualificationInput, setQualificationInput] = useState('');
    const [newQualificationSuffix, setNewQualificationSuffix] = useState('');
    const [submitError, setSubmitError] = useState('');


    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        address: {
            street: 'Auto-Union-Straße',
            streetNumber: '1',
            postalCode: '85057',
            city: 'Ingolstadt',
            country: 'Deutschland',
        },
        role: role,
        retired: false,
        overtime: 0,
        vacation: 0,
        languages: [],
        qualifications: [],
    });


    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));
    };

    const handleAddLanguage = (langCode) => {
        if (!formData.languages.includes(langCode)) {
            setFormData(prev => ({
                ...prev,
                languages: [...prev.languages, langCode]
            }));
        }
    };

    const handleRemoveLanguage = (langCode) => {
        setFormData(prev => ({
            ...prev,
            languages: prev.languages.filter(l => l !== langCode)
        }));
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowLanguageSelect(false);
            }
        };

        if (showLanguageSelect) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showLanguageSelect]);

    useEffect(() => {
        if (!isFlipped) return;

        const handleClickOutside = (event) => {
            if (cardRef.current && !cardRef.current.contains(event.target)) {
                setIsFlipped(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isFlipped]);

    useEffect(() => {
        const fetchQualifications = async () => {
            try {
                const res = await apiClient.get('/api/utility/allQualifications');
                if (res.status === 200) {
                    const names = res.data.map(q => q.name);
                    setAllQualifications(names);
                }
            } catch (err) {
                console.error("Fehler beim Laden der Qualifikationen", err);
            }
        };

        fetchQualifications();
    }, []);

    const handleSave = async () => {
        const newErrors = {};
        if (!formData.lastName.trim()) newErrors.lastName = 'Nachname erforderlich';
        if (
            !formData.address.street.trim() ||
            !formData.address.streetNumber.trim() ||
            !formData.address.postalCode.trim() ||
            !formData.address.city.trim() ||
            !formData.address.country.trim()
        ) {
            newErrors.address = 'Adresse unvollständig';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const payload = {
            ...formData,
            location: stringifyAddress(formData.address),
        };

        try {
            const response = await apiClient.post('/api/employee/addEmployee', payload);
            if (response.status === 200) {
                onEmployeeAdded();
                setFormData({
                    firstName: '',
                    lastName: '',
                    address: {
                        street: '',
                        streetNumber: '',
                        postalCode: '',
                        city: '',
                        country: '',
                    },
                    role: role,
                    overtime: 0,
                    vacation: 0,
                    languages: [],
                    qualifications: [],
                });
                setErrors({});
                setSubmitError('');
                setIsFlipped(false);
            }
        } catch (err) {
            console.error('Fehler beim Hinzufügen des Mitarbeiters', err);
            const msg = err?.response?.data || 'Überprüfen Sie die übermittelten Daten und versuchen Sie es erneut';
            setSubmitError(msg);
        }
    };


    return (
        <div className="w-full">
            <div className="w-full" ref={cardRef}>
                <div className="relative w-full h-[500px] [perspective:1000px]">
                    <div
                        className={`relative w-full h-full transition-transform duration-700 [transform-style:preserve-3d] ${
                            isFlipped ? "[transform:rotateY(180deg)]" : ""
                        }`}
                    >
                        {/* Front: PLUS BUTTON */}
                        <div
                            onClick={() => setIsFlipped(true)}
                            className="absolute w-full h-full [backface-visibility:hidden] bg-white shadow-md rounded-xl p-4 flex items-center justify-center cursor-pointer hover:bg-gray-50"
                        >
                            <img src={Plus} alt="plus" className="h-12 w-12 text-indigo-500"/>
                        </div>

                        {/* Back: FORM */}
                        <div
                            className="absolute w-full h-full [backface-visibility:hidden] [transform:rotateY(180deg)] bg-white shadow-md rounded-xl p-4 flex flex-col justify-between">
                            <div className="space-y-4">
                                <input
                                    name="firstName"
                                    placeholder="Vorname"
                                    value={formData.firstName}
                                    onChange={(e) => {
                                        handleChange(e);
                                        if (errors.firstName) setErrors(prev => ({...prev, firstName: undefined}));
                                    }}
                                    className={`w-full border rounded p-2 text-sm ${errors.firstName ? 'border-red-500' : ''}`}
                                />
                                {errors.firstName && (
                                    <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                                )}

                                <input
                                    name="lastName"
                                    placeholder="Nachname"
                                    value={formData.lastName}
                                    onChange={(e) => {
                                        handleChange(e);
                                        if (errors.lastName) setErrors(prev => ({...prev, lastName: undefined}));
                                    }}
                                    className={`w-full border rounded p-2 text-sm ${errors.lastName ? 'border-red-500' : ''}`}
                                />
                                {errors.lastName && (
                                    <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                                )}


                                {/* Adresse anzeigen und bearbeiten */}
                                <div className="flex items-center text-sm text-gray-700 space-x-2">
                                    <img src={Pin} alt="pin" className="h-5 w-5"/>
                                    <button
                                        type="button"
                                        onClick={() => setShowAddressModal(true)}
                                        className="text-left hover:underline hover:text-indigo-600 cursor-pointer line-clamp-2"
                                    >
                                        {formData.address?.street
                                            ? formatAddress(formData.address).replace(/\n/g, ', ')
                                            : "Adresse hinzufügen"}
                                    </button>
                                    {errors.address && (
                                        <p className="text-red-500 text-xs mt-1">{errors.address}</p>
                                    )}
                                </div>

                                {/* Overtime & Vacation */}
                                <div className="grid grid-cols-2 gap-4 mt-4">
                                    <div className="flex items-center space-x-2">
                                        <img src={Clock} alt="Überstunden" className="h-5 w-5"/>
                                        <input
                                            type="number"
                                            name="overtime"
                                            value={formData.overtime}
                                            onChange={handleChange}
                                            className={`w-full rounded-md border px-2 py-1 text-sm focus:ring-0 ${
                                                formData.overtime < 0
                                                    ? 'border-red-300 text-red-600'
                                                    : 'border-gray-300 text-gray-800'
                                            }`}/>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <img src={Sun} alt="Urlaubstage" className="h-5 w-5"/>
                                        <input
                                            type="number"
                                            name="vacation"
                                            value={formData.vacation}
                                            onChange={handleChange}
                                            className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-800 focus:border-indigo-500 focus:ring-0"
                                        />
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <div
                                        onClick={() => setShowQualificationModal(true)}
                                        className="relative flex items-center gap-2 overflow-x-auto max-w-full pr-6 cursor-pointer group flex-nowrap"
                                    >
                                        {(() => {
                                            const uniqueBaseNames = new Map();
                                            (formData.qualifications || []).forEach((q) => {
                                                const match = q.match(/^(.*?)(?:_(KL|M|GM))?$/);
                                                const base = (match && match[1]) || q;
                                                const suffix = (match && match[2]) || null;
                                                const priority = {KL: 3, M: 2, GM: 1, DEFAULT: 0};
                                                const current = uniqueBaseNames.get(base);
                                                if (!current || priority[suffix] > priority[current.suffix]) {
                                                    uniqueBaseNames.set(base, {full: q, base, suffix});
                                                }
                                            });

                                            const colorMap = {
                                                KL: "bg-indigo-500 text-white",
                                                DEFAULT: "bg-indigo-100 text-indigo-800",
                                                M: "bg-emerald-200 text-emerald-900",
                                                GM: "bg-rose-200 text-rose-900",
                                            };


                                            const pills = Array.from(uniqueBaseNames.values());
                                            const visibleCount = Math.min(pills.length, 2);
                                            const visiblePills = pills.slice(0, visibleCount);

                                            const displayPills = [...visiblePills, {base: "...", suffix: "DEFAULT"}];

                                            return displayPills.map(({base, suffix}, idx) => (
                                                <span
                                                    key={idx}
                                                    className={`text-xs px-3 py-1 rounded-full whitespace-nowrap transition-opacity ${
                                                        colorMap[suffix] || colorMap.DEFAULT
                                                    }`}
                                                    title="Klicken zum Bearbeiten"
                                                >
                                                {base}
                                            </span>
                                            ));
                                        })()}

                                        {formData.qualifications.length > 3 && (
                                            <div
                                                className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-r from-transparent to-white pointer-events-none rounded-r"></div>
                                        )}
                                    </div>
                                </div>


                                {/* Sprachen Auswahl */}
                                <div className="flex flex-wrap items-center gap-2">
                                    {formData.languages.map((lang) => (
                                        <button
                                            key={lang}
                                            onClick={() => handleRemoveLanguage(lang)}
                                            className="flex items-center space-x-1 bg-indigo-100 text-indigo-800 text-sm px-2 py-1 rounded-full hover:bg-red-100 hover:text-red-600 transition"
                                            title="Klicken zum Entfernen"
                                        >
                                            <span>{languageFlags[lang] || lang}</span>
                                            <span className="text-xs font-bold">&times;</span>
                                        </button>
                                    ))}
                                    <div className="relative" ref={dropdownRef}>
                                        <button
                                            type="button"
                                            onClick={() => setShowLanguageSelect((prev) => !prev)}
                                            className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-600 hover:bg-indigo-500 hover:text-white transition"
                                            title="Sprache hinzufügen"
                                        >
                                            +
                                        </button>

                                        {showLanguageSelect && (
                                            <div
                                                className="absolute left-0 mt-2 z-20 bg-white border border-gray-200 rounded-md shadow-lg p-2 max-h-48 overflow-y-auto w-48">
                                                {Object.keys(languageFlags)
                                                    .filter((lang) => !formData.languages.includes(lang))
                                                    .map((lang) => (
                                                        <button
                                                            key={lang}
                                                            onClick={() => {
                                                                handleAddLanguage(lang);
                                                                setShowLanguageSelect(false);
                                                            }}
                                                            className="w-full text-left text-sm px-2 py-1 rounded hover:bg-indigo-100 hover:text-indigo-800 transition"
                                                        >
                                                            {languageFlags[lang]} {lang}
                                                        </button>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                </div>


                                {/* Rolle */}
                                <div className="text-sm text-gray-500 italic pt-2">
                                    Rolle: <span className="font-semibold">{getRoleLabel(role)}</span>
                                </div>
                            </div>
                            {submitError && (
                                <div className="text-sm text-red-600 text-center mt-2">
                                    {submitError}
                                </div>
                            )}
                            {/* Save Button */}
                            <div className="flex justify-center mt-6">
                                <button
                                    type="button"
                                    className="rounded-full bg-indigo-500 px-4 py-2 text-xs text-white shadow-md hover:bg-indigo-400"
                                    onClick={handleSave}
                                >
                                    <img src={Check} alt="save" className="h-5 w-5"/>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Address Modal */}
                {showAddressModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10">
                        <div
                            className="relative bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl border border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">Adresse eingeben</h2>

                            <div className="grid grid-cols-2 gap-3">
                                <input
                                    placeholder="Straße"
                                    value={formData.address.street}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            address: {...prev.address, street: e.target.value},
                                        }))
                                    }
                                    className="col-span-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                />
                                <input
                                    placeholder="Nr."
                                    value={formData.address.streetNumber}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            address: {...prev.address, streetNumber: e.target.value},
                                        }))
                                    }
                                    className="col-span-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                />
                                <input
                                    placeholder="PLZ"
                                    value={formData.address.postalCode}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            address: {...prev.address, postalCode: e.target.value},
                                        }))
                                    }
                                    className="col-span-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                />
                                <input
                                    placeholder="Stadt"
                                    value={formData.address.city}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            address: {...prev.address, city: e.target.value},
                                        }))
                                    }
                                    className="col-span-1 rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                />
                                <input
                                    placeholder="Land"
                                    value={formData.address.country}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            address: {...prev.address, country: e.target.value},
                                        }))
                                    }
                                    className="col-span-2 rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                                />
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowAddressModal(false)}
                                    className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    onClick={() => setShowAddressModal(false)}
                                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                                >
                                    Speichern
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {showQualificationModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-white/10">
                        <div
                            className="relative bg-white rounded-xl p-6 w-[90%] max-w-md shadow-xl border border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-800 mb-4">Qualifikationen bearbeiten</h2>

                            <div className="flex flex-wrap gap-2 mb-4 max-h-[150px] overflow-y-auto pr-1">
                                {formData.qualifications.map((q, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-indigo-100 text-indigo-800"
                                    >
                                        <span>{q}</span>
                                        <button
                                            onClick={() => setFormData((prev) => ({
                                                ...prev,
                                                qualifications: prev.qualifications.filter((item) => item !== q)
                                            }))}
                                            className="ml-1 text-xs font-bold hover:text-red-600 transition"
                                            title="Entfernen"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Dropdown für vorhandene Optionen */}
                            <select
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                onChange={(e) => {
                                    const selected = e.target.value;
                                    if (selected && !formData.qualifications.includes(selected)) {
                                        setFormData((prev) => ({
                                            ...prev,
                                            qualifications: [...prev.qualifications, selected]
                                        }));
                                    }
                                    e.target.value = "";
                                }}
                            >
                                <option value="">+ Qualifikation hinzufügen</option>
                                {allQualifications.map((q) => (
                                    <option key={q} value={q}>{q}</option>
                                ))}
                            </select>

                            {/* Eigene Qualifikation hinzufügen */}
                            <div className="flex items-center gap-2 mt-4">
                                <input
                                    type="text"
                                    value={qualificationInput}
                                    onChange={(e) => setQualificationInput(e.target.value)}
                                    placeholder="Neue Qualifikation"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && qualificationInput.trim()) {
                                            let newQ = qualificationInput.trim();
                                            if (newQualificationSuffix) {
                                                newQ = `${newQ}_${newQualificationSuffix}`;
                                            }

                                            if (!formData.qualifications.includes(newQ)) {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    qualifications: [...prev.qualifications, newQ],
                                                }));
                                            }

                                            setQualificationInput('');
                                            setNewQualificationSuffix('');
                                        }
                                    }}
                                    className="flex-1 rounded-md border px-3 py-2 text-sm border-indigo-300 bg-indigo-50 text-indigo-900"
                                />
                                <select
                                    value={newQualificationSuffix}
                                    onChange={(e) => setNewQualificationSuffix(e.target.value)}
                                    className="text-xs rounded-md border border-gray-300 px-2 py-[6px] bg-white text-gray-700"
                                >
                                    <option value="">Instruktor</option>
                                    <option value="KL">Kursleiter</option>
                                    <option value="M">Mechaniker</option>
                                    <option value="GM">Gästebetreuer</option>
                                </select>
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowQualificationModal(false)}
                                    className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
                                >
                                    Abbrechen
                                </button>
                                <button
                                    onClick={() => setShowQualificationModal(false)}
                                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                                >
                                    Speichern
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NewEmployeeCard;
